import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeft,
  User,
  Users,
  MapPin,
  CreditCard,
  FileText,
  Phone,
  Mail,
  Calendar,
  Briefcase,
  IdCard,
  Download,
  Check,
  GraduationCap,
  Edit3,
  Trash2,
  UserCheck,
  UserX,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  IndianRupee,
  Plus,
  X,
  Award,
  Eye
} from 'lucide-react';
import { studentsApi, schoolDataApi } from '../services/api';
import { Student, SchoolClass, AttendanceEntry, ExamResult, StudentFee, SubjectMark, FeeCategory } from '../types';
import { formatDate, formatCurrency, getAge, generateInvoicePdf, billTitle, billTypeBadgeStyle, billStatusBadgeStyle } from '../utils/helpers';
import { sortClasses } from '../utils/classUtils';
import { ConfirmDialog, InputDialog, Modal } from '../components/ui';
import StudentForm from '../components/students/StudentForm';
import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  format,
  addMonths,
  subMonths,
  getDay
} from 'date-fns';

type TabKey = 'details' | 'attendance' | 'results' | 'payments';

const getStatusStyles = (status: string) => {
  switch (status) {
    case 'Form_Pending':
      return 'bg-[#332711] text-[#f9daa3]';
    case 'Submitted':
      return 'bg-[#16212b] text-[#bfdbfe]';
    case 'Approved':
      return 'bg-[#173322] text-[#8ff0b8]';
    case 'Rejected':
      return 'bg-[#341a17] text-[#fbccc8]';
    default:
      return 'bg-[#28221a] text-[#b9ad9b]';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'Form_Pending':
      return 'Admission Form Pending';
    case 'Submitted':
      return 'Under Review';
    case 'Approved':
      return 'Admitted';
    case 'Rejected':
      return 'Rejected';
    default:
      return status;
  }
};

const computeGrade = (percentage: number): string => {
  if (percentage >= 90) return 'A+';
  if (percentage >= 80) return 'A';
  if (percentage >= 70) return 'B+';
  if (percentage >= 60) return 'B';
  if (percentage >= 50) return 'C';
  if (percentage >= 40) return 'D';
  return 'F';
};

const SectionCard: React.FC<{ icon: React.ElementType; title: string; children: React.ReactNode; action?: React.ReactNode }> = ({
  icon: Icon,
  title,
  children,
  action
}) => (
  <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
    <div className="flex items-center justify-between mb-5">
      <h3 className="text-sm font-semibold text-[#f3ede2] flex items-center">
        <Icon className="w-4 h-4 mr-2 text-[#e2916d]" />
        {title}
      </h3>
      {action}
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">{children}</div>
  </div>
);

const Field: React.FC<{ label: string; value?: React.ReactNode; fullWidth?: boolean }> = ({ label, value, fullWidth }) => (
  <div className={fullWidth ? 'sm:col-span-2' : ''}>
    <p className="text-xs text-[#8a7f6e] mb-1">{label}</p>
    <p className="text-sm text-[#f3ede2]">{value || value === 0 ? value : <span className="text-[#5c5346]">Not provided</span>}</p>
  </div>
);

const documentList = (student: Student) => [
  { label: 'Student Photo', url: student.photoURL },
  { label: 'Aadhar Card', url: student.aadharCardURL },
  { label: 'Birth Certificate', url: student.birthCertificateURL },
  { label: 'Transfer Certificate', url: student.transferCertificateURL },
  { label: 'Previous Marksheet', url: student.marksheetURL },
  { label: 'Medical Certificate', url: student.medicalCertificateURL },
  { label: 'Caste Certificate', url: student.casteCertificateURL },
  { label: 'Income Certificate', url: student.incomeCertificateURL }
].filter(doc => !!doc.url);

const StudentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [student, setStudent] = useState<Student | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabKey>('details');
  const [classes, setClasses] = useState<SchoolClass[]>([]);

  // Details tab actions
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: 'danger' | 'warning' | 'info';
    confirmText?: string;
    cancelText?: string;
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [inputDialog, setInputDialog] = useState<{
    isOpen: boolean;
    title: string;
    message?: string;
    placeholder?: string;
    defaultValue?: string;
    options?: string[];
    onSubmit: (value: string) => void;
  }>({ isOpen: false, title: '', onSubmit: () => {} });

  // Attendance tab
  const [attendanceRecords, setAttendanceRecords] = useState<{ date: string; status: AttendanceEntry['status']; className: string; section: string }[]>([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  // Results tab
  const [results, setResults] = useState<ExamResult[]>([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [isAddingResult, setIsAddingResult] = useState(false);
  const [savingResult, setSavingResult] = useState(false);
  const [academicYear, setAcademicYear] = useState('');
  const [resultForm, setResultForm] = useState<{
    class: string;
    examName: string;
    subjects: SubjectMark[];
    remarks: string;
  }>({
    class: '',
    examName: '',
    subjects: [{ subject: '', maxMarks: 100, marksObtained: 0 }],
    remarks: ''
  });

  // Payments tab
  const [studentFees, setStudentFees] = useState<StudentFee[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [editingBill, setEditingBill] = useState<StudentFee | null>(null);
  const [editForm, setEditForm] = useState<{ dueDate: string; description: string; categories: FeeCategory[] }>({
    dueDate: '',
    description: '',
    categories: []
  });
  const [savingBill, setSavingBill] = useState(false);

  useEffect(() => {
    if (!id) return;
    loadStudent(id);
    schoolDataApi.classes.getAll().then(data => setClasses(sortClasses(data))).catch(() => setClasses([]));
    schoolDataApi.settings.getAcademicYear().then(setAcademicYear).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!id) return;
    if (activeTab === 'attendance' && attendanceRecords.length === 0) loadAttendance(id);
    if (activeTab === 'results' && results.length === 0) loadResults(id);
    if (activeTab === 'payments' && studentFees.length === 0) loadPayments(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id]);

  const loadStudent = async (studentId: string) => {
    try {
      setLoading(true);
      const data = await studentsApi.getById(studentId);
      if (!data) {
        toast.error('Student not found');
        navigate('/admin/students');
        return;
      }
      setStudent(data);
      setResultForm(prev => ({ ...prev, class: data.class }));
    } catch (error) {
      toast.error('Failed to load student');
      console.error('Error loading student:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAttendance = async (studentId: string) => {
    try {
      setLoadingAttendance(true);
      const data = await schoolDataApi.attendance.getForStudent(studentId);
      setAttendanceRecords(data);
    } catch (error) {
      toast.error('Failed to load attendance');
      console.error('Error loading attendance:', error);
    } finally {
      setLoadingAttendance(false);
    }
  };

  const loadResults = async (studentId: string) => {
    try {
      setLoadingResults(true);
      const data = await schoolDataApi.results.getByStudentId(studentId);
      setResults(data);
    } catch (error) {
      toast.error('Failed to load results');
      console.error('Error loading results:', error);
    } finally {
      setLoadingResults(false);
    }
  };

  const loadPayments = async (studentId: string) => {
    try {
      setLoadingPayments(true);
      const data = await schoolDataApi.studentFees.getAllByStudentId(studentId);
      setStudentFees(data);
    } catch (error) {
      toast.error('Failed to load payment history');
      console.error('Error loading payments:', error);
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleEditSubmit = async (data: any) => {
    if (!student) return;
    try {
      setSubmitting(true);
      await studentsApi.update(student.id, {
        name: data.name,
        dateOfBirth: data.dateOfBirth,
        gender: data.gender,
        class: data.class,
        section: data.section,
        parentName: data.parentName,
        parentContact: data.parentContact,
        parentEmail: data.parentEmail,
        permanentAddress: data.permanentAddress,
        currentAddress: data.currentAddress
      });

      if (data.class !== student.class) {
        try {
          await schoolDataApi.studentFees.syncMonthlyFeeForStudent(student.id, data.class);
        } catch (error) {
          console.error('Error syncing monthly fee for student:', error);
        }
      }

      setStudent(prev => (prev ? { ...prev, ...data } : prev));
      setIsEditing(false);
      toast.success('Student updated successfully');
    } catch (error) {
      toast.error('Failed to update student');
      console.error('Error updating student:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = () => {
    if (!student) return;
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Student',
      message: `Are you sure you want to delete ${student.name}?\n\nThis action cannot be undone and will remove all student data.`,
      type: 'danger',
      confirmText: 'Yes, Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await studentsApi.delete(student.id);
          toast.success('Student deleted successfully');
          navigate('/admin/students');
        } catch (error) {
          toast.error('Failed to delete student');
          console.error('Error deleting student:', error);
        }
      }
    });
  };

  const handleApprove = () => {
    if (!student) return;
    setInputDialog({
      isOpen: true,
      title: 'Approve Student',
      message: `Select class assignment for ${student.name}:`,
      placeholder: 'Select class',
      defaultValue: student.class,
      options: classes.map(c => c.name),
      onSubmit: (classAssigned) => {
        setInputDialog(prev => ({ ...prev, isOpen: false }));

        const proceedToConfirm = (section?: string) => {
          setConfirmDialog({
            isOpen: true,
            title: 'Approve Student',
            message: `Are you sure you want to approve ${student.name} for ${classAssigned}${section ? ` - Section ${section}` : ''}?`,
            type: 'info',
            confirmText: 'Yes, Approve',
            cancelText: 'Cancel',
            onConfirm: async () => {
              try {
                await studentsApi.approve(student.id, classAssigned, section);
                setStudent(prev =>
                  prev
                    ? { ...prev, admissionStatus: 'Approved', class: classAssigned, ...(section ? { section } : {}), admissionDate: new Date().toISOString() }
                    : prev
                );
                toast.success('Student admission approved');
              } catch (error) {
                toast.error('Failed to approve student');
                console.error('Error approving student:', error);
              }
            }
          });
        };

        const matchedClass = classes.find(c => c.name === classAssigned);
        const availableSections = matchedClass?.sections || [];

        if (availableSections.length > 0) {
          setTimeout(() => {
            setInputDialog({
              isOpen: true,
              title: 'Approve Student',
              message: `Select section for ${student.name} in ${classAssigned}:`,
              placeholder: 'Select section',
              defaultValue: student.section || '',
              options: availableSections,
              onSubmit: (section) => {
                setInputDialog(prev => ({ ...prev, isOpen: false }));
                proceedToConfirm(section || undefined);
              }
            });
          }, 0);
        } else {
          proceedToConfirm();
        }
      }
    });
  };

  const handleReject = () => {
    if (!student) return;
    setConfirmDialog({
      isOpen: true,
      title: 'Reject Student Admission',
      message: `Are you sure you want to reject ${student.name}'s admission?\n\nThis action will change their status to rejected.`,
      type: 'danger',
      confirmText: 'Yes, Reject',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await studentsApi.reject(student.id);
          setStudent(prev => (prev ? { ...prev, admissionStatus: 'Rejected' } : prev));
          toast.success('Student admission rejected');
        } catch (error) {
          toast.error('Failed to reject student');
          console.error('Error rejecting student:', error);
        }
      }
    });
  };

  // ---- Attendance derived data ----
  const attendanceMap = useMemo(() => {
    const map = new Map<string, AttendanceEntry['status']>();
    attendanceRecords.forEach(r => map.set(r.date, r.status));
    return map;
  }, [attendanceRecords]);

  const attendanceSummary = useMemo(() => {
    const total = attendanceRecords.length;
    const present = attendanceRecords.filter(r => r.status === 'Present').length;
    const absent = attendanceRecords.filter(r => r.status === 'Absent').length;
    const leave = attendanceRecords.filter(r => r.status === 'Leave').length;
    return {
      total,
      present,
      absent,
      leave,
      percentage: total > 0 ? Math.round((present / total) * 100) : 0
    };
  }, [attendanceRecords]);

  const calendarDays = useMemo(() => {
    const start = startOfMonth(calendarMonth);
    const end = endOfMonth(calendarMonth);
    const days = eachDayOfInterval({ start, end });
    const leadingBlanks = getDay(start);
    return { days, leadingBlanks };
  }, [calendarMonth]);

  const attendanceStatusColor = (status?: AttendanceEntry['status']) => {
    switch (status) {
      case 'Present':
        return 'bg-[#173322] text-[#8ff0b8] border-[#1f4a30]';
      case 'Absent':
        return 'bg-[#341a17] text-[#fbccc8] border-[#5a2b26]';
      case 'Leave':
        return 'bg-[#332711] text-[#f9daa3] border-[#4a3a19]';
      default:
        return 'bg-[#17140f] text-[#5c5346] border-[#3a3122]';
    }
  };

  // ---- Results form helpers ----
  const updateResultSubject = (index: number, field: keyof SubjectMark, value: string | number) => {
    setResultForm(prev => ({
      ...prev,
      subjects: prev.subjects.map((s, i) => (i === index ? { ...s, [field]: value } : s))
    }));
  };

  const addResultSubjectRow = () => {
    setResultForm(prev => ({ ...prev, subjects: [...prev.subjects, { subject: '', maxMarks: 100, marksObtained: 0 }] }));
  };

  const removeResultSubjectRow = (index: number) => {
    setResultForm(prev => ({ ...prev, subjects: prev.subjects.filter((_, i) => i !== index) }));
  };

  const resultTotals = useMemo(() => {
    const totalMax = resultForm.subjects.reduce((sum, s) => sum + (Number(s.maxMarks) || 0), 0);
    const totalObtained = resultForm.subjects.reduce((sum, s) => sum + (Number(s.marksObtained) || 0), 0);
    const percentage = totalMax > 0 ? Math.round((totalObtained / totalMax) * 10000) / 100 : 0;
    return { totalMax, totalObtained, percentage, grade: computeGrade(percentage) };
  }, [resultForm.subjects]);

  const handleSaveResult = async () => {
    if (!student) return;
    if (!resultForm.examName.trim() || !resultForm.class || resultForm.subjects.some(s => !s.subject.trim())) {
      toast.error('Please fill exam name, class and every subject name');
      return;
    }
    try {
      setSavingResult(true);
      const created = await schoolDataApi.results.create({
        studentId: student.id,
        class: resultForm.class,
        section: student.section,
        academicYear,
        examName: resultForm.examName.trim(),
        subjects: resultForm.subjects,
        totalMax: resultTotals.totalMax,
        totalObtained: resultTotals.totalObtained,
        percentage: resultTotals.percentage,
        grade: resultTotals.grade,
        remarks: resultForm.remarks.trim() || undefined
      });
      setResults(prev => [created, ...prev]);
      setIsAddingResult(false);
      setResultForm({
        class: student.class,
        examName: '',
        subjects: [{ subject: '', maxMarks: 100, marksObtained: 0 }],
        remarks: ''
      });
      toast.success('Result added');
    } catch (error) {
      toast.error('Failed to add result');
      console.error('Error adding result:', error);
    } finally {
      setSavingResult(false);
    }
  };

  const handleDeleteResult = (result: ExamResult) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Result',
      message: `Remove "${result.examName}" (${result.academicYear})? This cannot be undone.`,
      type: 'danger',
      confirmText: 'Yes, Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await schoolDataApi.results.delete(result.id);
          setResults(prev => prev.filter(r => r.id !== result.id));
          toast.success('Result deleted');
        } catch (error) {
          toast.error('Failed to delete result');
          console.error('Error deleting result:', error);
        }
      }
    });
  };

  const resultsByClass = useMemo(() => {
    const groups = new Map<string, ExamResult[]>();
    results.forEach(r => {
      const list = groups.get(r.class) || [];
      list.push(r);
      groups.set(r.class, list);
    });
    return Array.from(groups.entries());
  }, [results]);

  const feesBySession = useMemo(() => {
    return [...studentFees].sort((a, b) => {
      const yearCompare = b.academicYear.localeCompare(a.academicYear);
      if (yearCompare !== 0) return yearCompare;
      const monthCompare = (b.month || '').localeCompare(a.month || '');
      if (monthCompare !== 0) return monthCompare;
      return (a.billType || 'monthly').localeCompare(b.billType || 'monthly');
    });
  }, [studentFees]);

  const handleViewInvoice = (bill: StudentFee) => {
    if (!student) return;
    const doc = generateInvoicePdf(bill, student, billTitle(bill));
    window.open(doc.output('bloburl'), '_blank');
  };

  const handleOpenEditBill = (bill: StudentFee) => {
    setEditingBill(bill);
    setEditForm({
      dueDate: bill.dueDate || '',
      description: bill.description || '',
      categories: bill.feeStructure.map(c => ({ ...c }))
    });
  };

  const updateEditCategory = (index: number, field: keyof FeeCategory, value: string | number) => {
    setEditForm(prev => ({
      ...prev,
      categories: prev.categories.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    }));
  };

  const addEditCategory = () => setEditForm(prev => ({ ...prev, categories: [...prev.categories, { category: '', amount: 0 }] }));
  const removeEditCategory = (index: number) =>
    setEditForm(prev => ({ ...prev, categories: prev.categories.filter((_, i) => i !== index) }));

  const editTotal = editForm.categories.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);

  const handleSaveEditBill = async () => {
    if (!editingBill) return;
    if (editForm.categories.some(c => !c.category.trim() || c.amount <= 0)) {
      toast.error('Every charge needs a category name and an amount greater than 0');
      return;
    }
    try {
      setSavingBill(true);
      await schoolDataApi.studentFees.update(editingBill.id, {
        dueDate: editForm.dueDate || undefined,
        description: (editingBill.billType || 'monthly') === 'custom' ? editForm.description.trim() : undefined,
        feeCategories: editForm.categories
      });
      const totalFees = editTotal;
      const pendingAmount = Math.max(totalFees - editingBill.paidAmount, 0);
      const status: StudentFee['status'] = pendingAmount <= 0 ? 'Paid' : editingBill.paidAmount > 0 ? 'Partial' : 'Pending';
      setStudentFees(prev =>
        prev.map(f =>
          f.id === editingBill.id
            ? {
                ...f,
                dueDate: editForm.dueDate || undefined,
                description: (editingBill.billType || 'monthly') === 'custom' ? editForm.description.trim() : f.description,
                feeStructure: editForm.categories,
                totalFees,
                pendingAmount,
                status
              }
            : f
        )
      );
      setEditingBill(null);
      toast.success('Bill updated successfully');
    } catch (error) {
      toast.error('Failed to update bill');
      console.error('Error updating bill:', error);
    } finally {
      setSavingBill(false);
    }
  };

  const handleDeleteBill = (bill: StudentFee) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Bill',
      message: `Delete "${billTitle(bill)}"?\n\nThis will permanently remove the bill. Any payments already recorded against it will remain in the payments log but will no longer be linked to a bill.`,
      type: 'danger',
      confirmText: 'Yes, Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await schoolDataApi.studentFees.delete(bill.id);
          setStudentFees(prev => prev.filter(f => f.id !== bill.id));
          toast.success('Bill deleted');
        } catch (error) {
          toast.error('Failed to delete bill');
          console.error('Error deleting bill:', error);
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!student) return null;

  if (isEditing) {
    return (
      <div className="space-y-6">
        <div>
          <button
            onClick={() => setIsEditing(false)}
            className="flex items-center text-sm text-[#b9ad9b] hover:text-[#f3ede2] transition-colors mb-3"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back to Details
          </button>
        </div>
        <StudentForm
          onSubmit={handleEditSubmit}
          loading={submitting}
          initialData={{
            name: student.name,
            dateOfBirth: student.dateOfBirth,
            gender: student.gender,
            class: student.class,
            section: student.section,
            parentName: student.parentName,
            parentContact: student.parentContact,
            parentEmail: student.parentEmail,
            permanentAddress: student.permanentAddress,
            currentAddress: student.currentAddress
          }}
          onCancel={() => setIsEditing(false)}
        />
      </div>
    );
  }

  const documents = documentList(student);
  const tabs: { key: TabKey; label: string }[] = [
    { key: 'details', label: 'Details' },
    { key: 'attendance', label: 'Attendance' },
    { key: 'results', label: 'Results' },
    { key: 'payments', label: 'Payments' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/admin/students')}
          className="flex items-center text-sm text-[#b9ad9b] hover:text-[#f3ede2] transition-colors mb-3"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Student Management
        </button>
      </div>

      <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6 flex flex-wrap items-center gap-5">
        <div className="w-16 h-16 rounded-xl bg-[#332217] flex items-center justify-center overflow-hidden flex-shrink-0">
          {student.photoURL ? (
            <img src={student.photoURL} alt={student.name} className="w-full h-full object-cover" />
          ) : (
            <User className="w-7 h-7 text-[#e2916d]" />
          )}
        </div>
        <div className="flex-1 min-w-[200px]">
          <h1 className="text-xl font-semibold text-[#f3ede2]">{student.name}</h1>
          <p className="text-sm text-[#b9ad9b]">
            {student.studentId} &middot; {student.class}{student.section ? ` - ${student.section}` : ''} &middot; {student.gender}
          </p>
        </div>
        <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${getStatusStyles(student.admissionStatus)}`}>
          {getStatusLabel(student.admissionStatus)}
        </span>
        {student.isRteQuota && (
          <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-[#241f36] text-[#d6bbfb] flex items-center gap-1.5">
            <Check className="w-3.5 h-3.5" />
            RTE 25% Quota
          </span>
        )}

        <div className="flex items-center gap-2 ml-auto">
          {student.admissionStatus === 'Submitted' && (
            <>
              <button
                onClick={handleApprove}
                className="flex items-center space-x-1.5 px-3 py-2 text-sm text-[#4ade80] hover:bg-[#173322] border border-[#3a3122] rounded-lg transition-colors"
              >
                <UserCheck className="w-4 h-4" />
                <span>Approve</span>
              </button>
              <button
                onClick={handleReject}
                className="flex items-center space-x-1.5 px-3 py-2 text-sm text-[#f38b83] hover:bg-[#341a17] border border-[#3a3122] rounded-lg transition-colors"
              >
                <UserX className="w-4 h-4" />
                <span>Reject</span>
              </button>
            </>
          )}
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center space-x-1.5 px-3 py-2 text-sm text-[#b9ad9b] hover:bg-[#28221a] border border-[#3a3122] rounded-lg transition-colors"
          >
            <Edit3 className="w-4 h-4" />
            <span>Edit</span>
          </button>
          <button
            onClick={handleDelete}
            className="flex items-center space-x-1.5 px-3 py-2 text-sm text-[#f38b83] hover:bg-[#341a17] border border-[#3a3122] rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            <span>Delete</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-[#3a3122] overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeTab === tab.key ? 'border-[#e2916d] text-[#f3ede2]' : 'border-transparent text-[#8a7f6e] hover:text-[#b9ad9b]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Details tab */}
      {activeTab === 'details' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SectionCard icon={User} title="Student Details">
              <Field label="Date of Birth" value={student.dateOfBirth && `${formatDate(student.dateOfBirth)} (${getAge(student.dateOfBirth)} yrs)`} />
              <Field label="Aadhar Number" value={student.aadharNumber} />
              <Field label="Category" value={student.category} />
              <Field label="Caste" value={student.caste} />
              <Field label="Religion" value={student.religion} />
              <Field label="Previous School" value={student.previousSchool} />
              <Field label="PEN Number" value={student.penNumber} />
              <Field label="APAAR ID" value={student.apaarId} />
              <Field label="Medical Conditions" value={student.medicalConditions} fullWidth />
            </SectionCard>

            <SectionCard icon={Users} title="Parent Details">
              <Field label="Father's Name" value={student.fatherName} />
              <Field label="Father's Occupation" value={student.fatherOccupation} />
              <Field label="Mother's Name" value={student.motherName} />
              <Field label="Mother's Occupation" value={student.motherOccupation} />
              <Field
                label="Contact Number"
                value={student.parentContact && (
                  <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#8a7f6e]" />{student.parentContact}</span>
                )}
              />
              <Field
                label="Email Address"
                value={student.parentEmail && (
                  <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-[#8a7f6e]" />{student.parentEmail}</span>
                )}
              />
              <Field label="Emergency Contact" value={student.emergencyContact} />
            </SectionCard>

            <SectionCard icon={MapPin} title="Address">
              <Field label="Permanent Address" value={student.permanentAddress} fullWidth />
              <Field label="Current Address" value={student.currentAddress} fullWidth />
            </SectionCard>

            {Boolean(student.isRteQuota || student.annualIncome) && (
              <SectionCard icon={IdCard} title="RTE / EWS Details">
                <Field label="Applying under RTE Quota" value={student.isRteQuota ? 'Yes' : 'No'} />
                <Field label="Annual Family Income" value={student.annualIncome ? formatCurrency(student.annualIncome) : undefined} />
              </SectionCard>
            )}

            <SectionCard icon={Briefcase} title="Bank Details">
              <Field label="Account Holder Name" value={student.accountHolderName} />
              <Field label="Bank Name" value={student.bankName} />
              <Field label="Account Number" value={student.bankAccountNumber} />
              <Field label="IFSC Code" value={student.ifscCode} />
            </SectionCard>

            <SectionCard icon={CreditCard} title="Fee & Facilities">
              <Field label="Admission Fee" value={student.admissionFee ? formatCurrency(student.admissionFee) : undefined} />
              <Field label="Hostel Required" value={student.needsHostel ? 'Yes' : 'No'} />
              <Field label="Transport Required" value={student.needsTransport ? 'Yes' : 'No'} />
            </SectionCard>
          </div>

          <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
            <h3 className="text-sm font-semibold text-[#f3ede2] mb-5 flex items-center">
              <FileText className="w-4 h-4 mr-2 text-[#e2916d]" />
              Uploaded Documents
            </h3>
            {documents.length === 0 ? (
              <p className="text-sm text-[#8a7f6e]">No documents were uploaded with this application.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {documents.map(doc => (
                  <a
                    key={doc.label}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between gap-3 px-4 py-3 border border-[#3a3122] rounded-lg hover:border-[#e2916d] transition-colors"
                  >
                    <span className="text-sm text-[#f3ede2]">{doc.label}</span>
                    <Download className="w-4 h-4 text-[#8a7f6e] flex-shrink-0" />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
            <h3 className="text-sm font-semibold text-[#f3ede2] mb-5 flex items-center">
              <Calendar className="w-4 h-4 mr-2 text-[#e2916d]" />
              Timeline
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              <Field label="Application Submitted" value={student.createdAt && formatDate(student.createdAt)} />
              <Field label="Admission Confirmed" value={student.admissionDate ? formatDate(student.admissionDate) : undefined} />
            </div>
          </div>
        </div>
      )}

      {/* Attendance tab */}
      {activeTab === 'attendance' && (
        <div className="space-y-6">
          {loadingAttendance ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-4">
                  <p className="text-xs text-[#8a7f6e]">Total Marked Days</p>
                  <p className="text-xl font-bold text-[#f3ede2] mt-1">{attendanceSummary.total}</p>
                </div>
                <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-4">
                  <p className="text-xs text-[#8a7f6e]">Present</p>
                  <p className="text-xl font-bold text-[#4ade80] mt-1">{attendanceSummary.present}</p>
                </div>
                <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-4">
                  <p className="text-xs text-[#8a7f6e]">Absent</p>
                  <p className="text-xl font-bold text-[#f38b83] mt-1">{attendanceSummary.absent}</p>
                </div>
                <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-4">
                  <p className="text-xs text-[#8a7f6e]">Attendance %</p>
                  <p className="text-xl font-bold text-[#f3ede2] mt-1">{attendanceSummary.percentage}%</p>
                </div>
              </div>

              <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
                <div className="flex items-center justify-between mb-5">
                  <button
                    onClick={() => setCalendarMonth(prev => subMonths(prev, 1))}
                    className="p-2 text-[#8a7f6e] hover:text-[#f3ede2] hover:bg-[#28221a] rounded-lg transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <h3 className="text-sm font-semibold text-[#f3ede2]">{format(calendarMonth, 'MMMM yyyy')}</h3>
                  <button
                    onClick={() => setCalendarMonth(prev => addMonths(prev, 1))}
                    className="p-2 text-[#8a7f6e] hover:text-[#f3ede2] hover:bg-[#28221a] rounded-lg transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-7 gap-2 text-center text-xs text-[#8a7f6e] mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                    <div key={d}>{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {Array.from({ length: calendarDays.leadingBlanks }).map((_, i) => (
                    <div key={`blank-${i}`} />
                  ))}
                  {calendarDays.days.map(day => {
                    const dateStr = format(day, 'yyyy-MM-dd');
                    const status = attendanceMap.get(dateStr);
                    return (
                      <div
                        key={dateStr}
                        className={`aspect-square flex items-center justify-center rounded-lg border text-sm ${attendanceStatusColor(status)}`}
                        title={status || 'No record'}
                      >
                        {format(day, 'd')}
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-center gap-4 mt-5 text-xs text-[#8a7f6e]">
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#173322] border border-[#1f4a30] inline-block" />Present</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#341a17] border border-[#5a2b26] inline-block" />Absent</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#332711] border border-[#4a3a19] inline-block" />Leave</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#17140f] border border-[#3a3122] inline-block" />No record</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Results tab */}
      {activeTab === 'results' && (
        <div className="space-y-6">
          <div className="flex justify-end">
            <button
              onClick={() => setIsAddingResult(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-[#e2916d] hover:bg-[#f0a67d] text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Result</span>
            </button>
          </div>

          {loadingResults ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : resultsByClass.length === 0 ? (
            <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-12 text-center">
              <Award className="w-12 h-12 text-[#8a7f6e] mx-auto mb-4" />
              <p className="text-[#b9ad9b] text-lg">No results recorded yet</p>
              <p className="text-[#8a7f6e] text-sm">Use "Add Result" to record an exam result for this student</p>
            </div>
          ) : (
            resultsByClass.map(([className, classResults]) => (
              <div key={className} className="space-y-3">
                <h3 className="text-sm font-semibold text-[#b9ad9b] flex items-center">
                  <GraduationCap className="w-4 h-4 mr-2 text-[#e2916d]" />
                  {className}
                </h3>
                {classResults.map(result => (
                  <div key={result.id} className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-[#f3ede2] font-medium">{result.examName}</h4>
                        <p className="text-xs text-[#8a7f6e]">Academic Year {result.academicYear}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="px-3 py-1 rounded-full text-sm font-medium bg-[#16212b] text-[#bfdbfe]">
                          {result.percentage}% &middot; Grade {result.grade}
                        </span>
                        <button
                          onClick={() => handleDeleteResult(result)}
                          className="p-1.5 text-[#8a7f6e] hover:text-[#f38b83] hover:bg-[#341a17] rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-[#3a3122]">
                            <th className="text-left py-2 px-2 text-xs font-medium text-[#8a7f6e] uppercase">Subject</th>
                            <th className="text-right py-2 px-2 text-xs font-medium text-[#8a7f6e] uppercase">Max Marks</th>
                            <th className="text-right py-2 px-2 text-xs font-medium text-[#8a7f6e] uppercase">Obtained</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#3a3122]">
                          {result.subjects.map((s, i) => (
                            <tr key={i}>
                              <td className="py-2 px-2 text-[#f3ede2]">{s.subject}</td>
                              <td className="py-2 px-2 text-right text-[#b9ad9b]">{s.maxMarks}</td>
                              <td className="py-2 px-2 text-right text-[#f3ede2] font-medium">{s.marksObtained}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 border-[#e2916d]">
                            <td className="py-2 px-2 text-[#f3ede2] font-semibold">Total</td>
                            <td className="py-2 px-2 text-right text-[#b9ad9b] font-semibold">{result.totalMax}</td>
                            <td className="py-2 px-2 text-right text-[#e2916d] font-bold">{result.totalObtained}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    {result.remarks && <p className="text-sm text-[#8a7f6e] mt-3">Remarks: {result.remarks}</p>}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      )}

      {/* Payments tab */}
      {activeTab === 'payments' && (
        <div className="space-y-6">
          {loadingPayments ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : feesBySession.length === 0 ? (
            <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-12 text-center">
              <IndianRupee className="w-12 h-12 text-[#8a7f6e] mx-auto mb-4" />
              <p className="text-[#b9ad9b] text-lg">No fee records yet</p>
              <p className="text-[#8a7f6e] text-sm">Generate a bill for this student from Fee Management → Generate Bill</p>
            </div>
          ) : (
            <div className="bg-[#211c15] rounded-xl border border-[#3a3122] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#3a3122] bg-[#28221a]">
                      <th className="text-left py-3 px-4 text-xs font-medium text-[#8a7f6e] uppercase whitespace-nowrap">Bill</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-[#8a7f6e] uppercase whitespace-nowrap">Type</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-[#8a7f6e] uppercase whitespace-nowrap">Generated</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-[#8a7f6e] uppercase whitespace-nowrap">Due Date</th>
                      <th className="text-right py-3 px-4 text-xs font-medium text-[#8a7f6e] uppercase whitespace-nowrap">Amount</th>
                      <th className="text-right py-3 px-4 text-xs font-medium text-[#8a7f6e] uppercase whitespace-nowrap">Paid</th>
                      <th className="text-right py-3 px-4 text-xs font-medium text-[#8a7f6e] uppercase whitespace-nowrap">Pending</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-[#8a7f6e] uppercase whitespace-nowrap">Status</th>
                      <th className="text-right py-3 px-4 text-xs font-medium text-[#8a7f6e] uppercase whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#3a3122]">
                    {feesBySession.map(session => (
                      <tr key={session.id} className="hover:bg-[#28221a]/60 transition-colors">
                        <td className="py-3 px-4 whitespace-nowrap">
                          <p className="text-[#f3ede2] font-medium">{billTitle(session)}</p>
                          <p className="text-xs text-[#8a7f6e]">{session.billNumber || '—'} &middot; {session.className} &middot; {session.academicYear}</p>
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${billTypeBadgeStyle(session)}`}>
                            {(session.billType || 'monthly').charAt(0).toUpperCase() + (session.billType || 'monthly').slice(1)}
                          </span>
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-[#b9ad9b]">
                          {session.generatedAt ? formatDate(session.generatedAt) : '—'}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-[#b9ad9b]">
                          {session.dueDate ? formatDate(session.dueDate) : '—'}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-right text-[#f3ede2] font-medium">{formatCurrency(session.totalFees)}</td>
                        <td className="py-3 px-4 whitespace-nowrap text-right text-[#4ade80]">{formatCurrency(session.paidAmount)}</td>
                        <td className="py-3 px-4 whitespace-nowrap text-right text-[#f38b83]">{formatCurrency(session.pendingAmount)}</td>
                        <td className="py-3 px-4 whitespace-nowrap">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${billStatusBadgeStyle(session.status)}`}>
                            {session.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleViewInvoice(session)}
                              title="View Invoice"
                              className="p-1.5 text-[#8a7f6e] hover:text-[#60a5fa] hover:bg-[#16212b] rounded-lg transition-colors"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleOpenEditBill(session)}
                              title="Edit Bill"
                              className="p-1.5 text-[#8a7f6e] hover:text-[#e2916d] hover:bg-[#332217] rounded-lg transition-colors"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteBill(session)}
                              title="Delete Bill"
                              className="p-1.5 text-[#8a7f6e] hover:text-[#f38b83] hover:bg-[#341a17] rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Bill Modal */}
      <Modal isOpen={!!editingBill} onClose={() => setEditingBill(null)} title={editingBill ? `Edit ${billTitle(editingBill)}` : 'Edit Bill'} size="lg">
        {editingBill && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#b9ad9b] mb-2">Due Date</label>
                <input
                  type="date"
                  value={editForm.dueDate}
                  onChange={(e) => setEditForm(prev => ({ ...prev, dueDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                />
              </div>
              {(editingBill.billType || 'monthly') === 'custom' && (
                <div>
                  <label className="block text-sm font-medium text-[#b9ad9b] mb-2">Bill Title / Reason</label>
                  <input
                    type="text"
                    value={editForm.description}
                    onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                  />
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-[#b9ad9b]">Charges</label>
                <button
                  type="button"
                  onClick={addEditCategory}
                  className="text-xs px-2 py-1 border border-[#3a3122] text-[#b9ad9b] rounded-lg hover:bg-[#28221a] transition-colors flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Add Charge
                </button>
              </div>
              <div className="overflow-x-auto border border-[#3a3122] rounded-lg">
                <table className="w-full">
                  <thead className="bg-[#28221a]">
                    <tr>
                      <th className="text-left py-2 px-4 text-xs font-medium text-[#8a7f6e] uppercase">Category</th>
                      <th className="text-right py-2 px-4 text-xs font-medium text-[#8a7f6e] uppercase w-40">Amount (₹)</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#3a3122]">
                    {editForm.categories.map((c, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={c.category}
                            onChange={(e) => updateEditCategory(i, 'category', e.target.value)}
                            className="bg-transparent text-[#f3ede2] placeholder-[#8a7f6e] w-full px-2 py-1.5 border border-transparent hover:border-[#3a3122] focus:border-[#e2916d] rounded focus:outline-none"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            min={0}
                            value={c.amount}
                            onChange={(e) => updateEditCategory(i, 'amount', Number(e.target.value))}
                            className="bg-transparent text-[#f3ede2] w-full px-2 py-1.5 border border-transparent hover:border-[#3a3122] focus:border-[#e2916d] rounded focus:outline-none text-right"
                          />
                        </td>
                        <td className="px-2 py-2 text-center">
                          {editForm.categories.length > 1 && (
                            <button onClick={() => removeEditCategory(i)} className="p-1 text-[#8a7f6e] hover:text-[#f38b83] transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-[#e2916d] bg-[#28221a]">
                      <td className="px-4 py-2.5 text-[#f3ede2] font-medium">Total</td>
                      <td className="px-4 py-2.5 text-right text-[#e2916d] font-bold">{formatCurrency(editTotal)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {editingBill.paidAmount > 0 && (
                <p className="text-xs text-[#f0b64c] mt-2">
                  ₹{editingBill.paidAmount} already paid against this bill — pending amount will be recalculated from the new total.
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2 border-t border-[#3a3122]">
              <button
                onClick={() => setEditingBill(null)}
                className="px-4 py-2 text-[#8a7f6e] hover:text-[#b9ad9b] border border-[#3a3122] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEditBill}
                disabled={savingBill}
                className="px-4 py-2 bg-[#e2916d] text-white rounded-lg hover:bg-[#f0a67d] disabled:opacity-50 transition-colors"
              >
                {savingBill ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Add Result Modal */}
      <Modal isOpen={isAddingResult} onClose={() => setIsAddingResult(false)} title="Add Exam Result" size="lg">
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">Class</label>
              <select
                value={resultForm.class}
                onChange={(e) => setResultForm(prev => ({ ...prev, class: e.target.value }))}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
              >
                {classes.map(cls => (
                  <option key={cls.id} value={cls.name}>{cls.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">Academic Year</label>
              <input
                type="text"
                readOnly
                value={academicYear}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg bg-[#28221a] text-[#8a7f6e] cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">Exam Name</label>
              <input
                type="text"
                value={resultForm.examName}
                onChange={(e) => setResultForm(prev => ({ ...prev, examName: e.target.value }))}
                placeholder="e.g. Term 1, Half Yearly"
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-[#b9ad9b]">Subjects</label>
              <button
                type="button"
                onClick={addResultSubjectRow}
                className="text-xs px-2 py-1 border border-[#3a3122] text-[#b9ad9b] rounded-lg hover:bg-[#28221a] transition-colors flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Add Subject
              </button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {resultForm.subjects.map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={s.subject}
                    onChange={(e) => updateResultSubject(i, 'subject', e.target.value)}
                    placeholder="Subject name"
                    className="flex-1 px-3 py-2 text-sm border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                  />
                  <input
                    type="number"
                    min={0}
                    value={s.maxMarks}
                    onChange={(e) => updateResultSubject(i, 'maxMarks', Number(e.target.value))}
                    placeholder="Max"
                    className="w-24 px-3 py-2 text-sm border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                  />
                  <input
                    type="number"
                    min={0}
                    value={s.marksObtained}
                    onChange={(e) => updateResultSubject(i, 'marksObtained', Number(e.target.value))}
                    placeholder="Obtained"
                    className="w-24 px-3 py-2 text-sm border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                  />
                  {resultForm.subjects.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeResultSubjectRow(i)}
                      className="p-2 text-[#8a7f6e] hover:text-[#f38b83] transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#b9ad9b] mb-2">Remarks (optional)</label>
            <textarea
              value={resultForm.remarks}
              onChange={(e) => setResultForm(prev => ({ ...prev, remarks: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
            />
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-[#3a3122]">
            <p className="text-sm text-[#b9ad9b]">
              Total: <span className="text-[#f3ede2] font-medium">{resultTotals.totalObtained}/{resultTotals.totalMax}</span>
              {' '}&middot; {resultTotals.percentage}% &middot; Grade {resultTotals.grade}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setIsAddingResult(false)}
                className="px-4 py-2 text-[#8a7f6e] hover:text-[#b9ad9b] border border-[#3a3122] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveResult}
                disabled={savingResult}
                className="px-4 py-2 bg-[#e2916d] text-white rounded-lg hover:bg-[#f0a67d] disabled:opacity-50 transition-colors"
              >
                {savingResult ? 'Saving...' : 'Save Result'}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
      />

      <InputDialog
        isOpen={inputDialog.isOpen}
        onClose={() => setInputDialog({ ...inputDialog, isOpen: false })}
        onSubmit={inputDialog.onSubmit}
        title={inputDialog.title}
        message={inputDialog.message}
        placeholder={inputDialog.placeholder}
        defaultValue={inputDialog.defaultValue}
        options={inputDialog.options}
      />
    </div>
  );
};

export default StudentDetail;
